import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Ban, Clock, Lock, MinusCircle, Scissors } from "lucide-react";

import type { DiagnosticSnapshot, SectionEnvelope, SectionSource } from "@/api/diagnostic";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { NO_VALUE, readSection } from "./sectionState";
import { SourceFooter } from "./SourceFooter";

export interface DiagnosticTabProps {
  /** As abas são **puramente apresentacionais**: recebem as seções por props e
   *  não chamam hook nenhum. É o que as mantém testáveis sem mock. */
  sections: DiagnosticSnapshot["sections"];
  snapshots?: DiagnosticSnapshot["snapshots"];
  /** #877: o bloco que o worker materializa quando alguém publica a
   *  Calibragem. Ausente quer dizer que não houve publicação e a tela está
   *  lendo o corrente, que é o comportamento de sempre. O Ato 5 usa isto para
   *  declarar a procedência de cada número de topo. */
  calibration?: DiagnosticSnapshot["calibration"];
  /** #1340: a decisão humana, um envelope por domínio. O backend a lê ao vivo
   *  mesmo com a Calibragem publicada, que congela só o dado de máquina. O
   *  `WricefCard` do Ato 1 é o primeiro card a ler este bloco (#1343, F5). */
  validation?: DiagnosticSnapshot["validation"];
  /** Só a tela de detalhe recebe: o snapshot ainda não chegou, ou a leitura dele
   *  falhou. Sem isso, `validation` ausente parece "não há análise", e o detalhe
   *  aberto por link mandaria esperar uma análise que já rodou (#1344). Os atos
   *  não recebem: o card fechado da `Section` já cobre esses dois casos. */
  snapshotState?: "loading" | "failed";
}

interface DiagnosticSectionProps {
  envelope: SectionEnvelope;
  title: ReactNode;
  /**
   * Segunda linha do cabeçalho: o recorte do card, em voz neutra.
   *
   * É o que diz "Execuções registradas em 12 meses" debaixo de "Vitalidade do
   * código customizado". Sem ele, cada ato enfiava o recorte dentro do próprio
   * conteúdo, com um tratamento diferente por ato.
   */
  subtitle?: ReactNode;
  /**
   * A frase que lê o número, entre o conteúdo e a linha de origem.
   *
   * **Só aparece com dado na tela.** Ela interpreta um número; num card fechado
   * viraria afirmação sobre dado que não existe, que é o defeito que esta tela
   * inteira existe para não cometer.
   */
  insight?: ReactNode;
  /** Detalhe que só a aba conhece, ex.: "9 of 12 · CDHDR failed". */
  detail?: ReactNode;
  actions?: ReactNode;
  snapshots?: DiagnosticSnapshot["snapshots"];
  /**
   * `false` quando o card **não consome a amostra** da seção, só agregados sobre
   * a população inteira (`by_coverage_type`, `by_lob`).
   *
   * Sem isto o card herdaria a linha "mostrando 3 de 40" do envelope e afirmaria
   * que o número foi feito com 3 gaps, quando foi feito com os 40. Rotular como
   * amostra o que não é amostra é a mesma mentira que o zero inventado, só que
   * ao contrário: subestima o dado em vez de superestimar.
   */
  showsSampling?: boolean;
  /**
   * `false` quando o card **não mostra a entidade que a origem cortou**.
   *
   * Um envelope serve vários cards, e o banner de `truncated` afirma algo sobre
   * os **números exibidos** — "o total real pode ser maior". Numa seção cortada
   * por uma entidade só (`org_structure` fica `truncated` quando
   * `purchasing_orgs` bate o teto de 100 do T024E), repetir o banner num card que
   * mostra empresas diz que a lista de empresas pode estar incompleta. T001 não
   * tem teto: é falso.
   *
   * Irmão de {@link showsSampling}, e pelo mesmo motivo — só que este erra para o
   * outro lado: superestima a incerteza em vez de subestimá-la. As duas mentiras
   * custam a mesma confiança.
   *
   * ⚠️ Não afeta o banner de `partial`, que diz "alguma fonte falhou" — verdade
   * sobre a seção inteira, sem afirmar nada sobre os números deste card.
   */
  showsSourceTruncation?: boolean;
  /**
   * A origem dos números do card, quando ela não é a da seção.
   *
   * O card principal do Ato 5 é a seção `bom_totals`, mas com a BOM do Pricing
   * App vigente os números de software saem do domínio `bom_lines`: a linha de
   * origem da seção apontaria para a BOM calculada, que o card não mostra mais.
   */
  source?: SectionSource;
  children?: ReactNode;
  className?: string;
}

const BANNER_TONE = "border-amber-200 bg-amber-50 text-amber-800";

/**
 * Casca de toda seção do Diagnóstico: decide o que renderizar a partir do estado
 * do envelope, para que as cinco abas não inventem cinco tratamentos diferentes.
 *
 * A copy visível é sempre localizada; o `reason` do backend (pt-BR, às vezes
 * mensagem SAP verbatim) entra como `title`, nunca como texto principal.
 */
export function DiagnosticSection({
  envelope,
  title,
  subtitle,
  insight,
  detail,
  actions,
  snapshots,
  showsSampling = true,
  showsSourceTruncation = true,
  source,
  children,
  className,
}: DiagnosticSectionProps) {
  const { t } = useTranslation();
  const render = readSection(envelope);

  const bannerCopy =
    render.state === "partial"
      ? t("Partial data — some sources failed to extract.")
      : render.state === "truncated" && showsSourceTruncation
        ? t("The source system truncated this result — the real total may be higher.")
        : null;

  return (
    <Card
      data-testid={`diagnostic-section-${render.state}`}
      data-state={render.state}
      className={cn("border shadow-none", className)}
    >
      <div className="flex items-start justify-between gap-3 px-5 pt-4">
        <div>
          <div className="text-sm font-semibold text-[#08254c]">{title}</div>
          {subtitle && (
            <div
              data-testid="diagnostic-section-subtitle"
              className="mt-0.5 text-[11px] leading-snug text-[#7b8799]"
            >
              {subtitle}
            </div>
          )}
        </div>
        {actions}
      </div>

      <div className="px-5 pb-4">
        {bannerCopy && (
          <div
            data-testid="diagnostic-section-banner"
            className={cn(
              "mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
              BANNER_TONE,
            )}
          >
            {render.state === "truncated" ? (
              <Scissors className="mt-px h-3.5 w-3.5 shrink-0" />
            ) : (
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            )}
            <span>
              {bannerCopy}
              {detail && <span className="ml-1 font-medium">{detail}</span>}
            </span>
          </div>
        )}

        {showsSampling && render.sampled && (
          <div
            data-testid="diagnostic-section-sampled"
            className="mt-3 font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]"
          >
            {t("Showing {{shown}} of {{total}} — the full set lives in its own endpoint.", {
              shown: render.sampled.shown,
              total: render.sampled.total,
            })}
          </div>
        )}

        {render.showsData ? (
          <div className="mt-3">{children}</div>
        ) : (
          <ClosedBody state={render.state} code={envelope.code} reason={render.reason} />
        )}

        {render.note && (
          <div className="mt-2 text-[11px] italic text-[#7b8799]" title={render.note}>
            {render.note}
          </div>
        )}

        {insight && render.showsData && (
          <p
            data-testid="diagnostic-section-insight"
            className="mt-3 text-[12px] leading-relaxed text-[#3d4f66]"
          >
            {insight}
          </p>
        )}

        <SourceFooter source={source ?? envelope.source} snapshots={snapshots} />
      </div>
    </Card>
  );
}

/**
 * `DiagnosticSection` com guarda de chave ausente — o mesmo defensivo que as
 * cinco abas copiavam byte a byte antes de virar componente único aqui.
 *
 * O contrato garante que **toda** seção existe sempre no payload, mas uma seção
 * nova no backend não pode derrubar a aba antes de o frontend saber renderizá-la.
 */
export function Section({
  sections,
  name,
  title,
  snapshots,
  children,
  ...rest
}: DiagnosticTabProps & {
  name: string;
  title: ReactNode;
  children?: ReactNode;
  // As duas partes da anatomia de card que a #872 criou. Elas viajam pelo
  // `...rest`, mas sem estarem no tipo o TypeScript recusa quem as passa —
  // e o primeiro card a usá-las descobriu isso.
  subtitle?: ReactNode;
  insight?: ReactNode;
  detail?: ReactNode;
  actions?: ReactNode;
  showsSampling?: boolean;
  showsSourceTruncation?: boolean;
  source?: SectionSource;
  className?: string;
}) {
  const envelope = (sections as Record<string, SectionEnvelope | undefined>)[name];
  if (!envelope) return null;
  return (
    <DiagnosticSection envelope={envelope} title={title} snapshots={snapshots} {...rest}>
      {children}
    </DiagnosticSection>
  );
}

/**
 * Corpo dos estados sem dado. `empty` é uma afirmação sobre a origem ("não há
 * registros"); os outros três são ausência de conhecimento, e aí o valor é `—`.
 *
 * `IN_PROGRESS` é o `not_collected` de quem ainda conta em segundo plano (o
 * Digital Access do ECC): o bloco existe, e dizer que nenhuma execução o produziu
 * seria falso.
 */
function ClosedBody({
  state,
  code,
  reason,
}: {
  state: SectionEnvelope["state"];
  code: string;
  reason: string;
}) {
  const { t } = useTranslation();
  const counting = state === "not_collected" && code === "IN_PROGRESS";

  const copy =
    state === "empty"
      ? t("No records in the source system.")
      : counting
        ? t("Counting in the background. The number appears when it finishes.")
        : state === "not_collected"
          ? t("Not collected — this block was not produced by any completed run.")
          : state === "unauthorized"
            ? t("Not authorized to read this block in the source system.")
            : t("This block could not be read from the source system.");

  const Icon = counting
    ? Clock
    : state === "empty"
      ? MinusCircle
      : state === "not_collected"
        ? Ban
        : state === "unauthorized"
          ? Lock
          : AlertTriangle;

  return (
    <div className="mt-3 flex items-start gap-3">
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          state === "error" || state === "unauthorized" ? "text-red-700" : "text-[#7b8799]",
        )}
      />
      <div className="min-w-0">
        {state !== "empty" && (
          <span
            data-testid="diagnostic-section-value"
            className="mr-2 font-brand text-2xl font-extrabold leading-none text-[#b2bccb]"
          >
            {NO_VALUE}
          </span>
        )}
        <span
          data-testid="diagnostic-section-reason"
          title={reason || undefined}
          className="text-xs text-[#596b85]"
        >
          {copy}
        </span>
      </div>
    </div>
  );
}
